import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import {
  ArrowRight,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Zap,
  Droplets,
  FileText,
  Activity,
  MessageSquare,
  Info,
} from "lucide-react";
import { cn } from "@shared/lib/utils";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { useBillChangesDetails, useGenerateCorrectedBill } from "../hook";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface ImpactAnalysisProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
}

export function ImpactAnalysis({
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
}: ImpactAnalysisProps) {
  const selectedBill = stepHelpers?.getStepData(0);
  const step1Data = stepHelpers?.getStepData(1);
  const { data: correctedData } = useBillChangesDetails(
    selectedBill?.correctionId
  );

  /* ------------------ Common ------------------ */
  const currency = useCurrency();
  const billDetail = stepHelpers?.getStepData(0);
  const { mutate: generateBill } = useGenerateCorrectedBill(
    billDetail?.correctionId
  );
  const financialImpact = correctedData?.financialImpact;
  const appliedCorrections = correctedData?.appliedCorrections;

  const originalTotal = financialImpact?.originalAmount ?? 0;
  const newTotal = financialImpact?.correctedAmount ?? 0;
  const difference = newTotal - originalTotal;

  const CORRECTION_TYPE_MAP: Record<number, string> = {
  0: "consumption",
  1: "service",
  2: "penalty",
  3: "data",
  4: "template",
};
  const selectedTypes: string[] =
    step1Data?.selectedCorrectionTypes?.map(
      (index: number) => CORRECTION_TYPE_MAP[index]
    ) ?? [];

  /* ------------------ Consumption ------------------ */
  const consumption = appliedCorrections?.consumption;

  const consumptionMeters =
    consumption?.meters?.map((meter: any) => ({
      id: meter.meterId,
      title: `${meter.utilityService || "NA"} Meter (${
        meter.meterNumber || "NA"
      })`,
      icon: Zap,
      currentReading: {
        original: meter.currentReading?.original ?? "NA",
        corrected: meter.currentReading?.corrected ?? "NA",
      },
      consumption: {
        original: meter.consumption?.original ?? "NA",
        corrected: meter.consumption?.corrected ?? "NA",
        unit: meter.unit || "",
      },
      reason: meter.reason || "NA",
    })) ?? [];

  /* ------------------ Penalty ------------------ */
  const lateFees = appliedCorrections?.lateFees;

  const lateFeeData = {
    original: lateFees?.latePaymentFee?.original ?? "NA",
    corrected: lateFees?.latePaymentFee?.corrected ?? "NA",
    reason: lateFees?.reason || "NA",
  };

  /* ------------------ Data Correction ------------------ */
  const consumerData = appliedCorrections?.consumerData;
  const serviceChargesData = appliedCorrections?.serviceCharges;
  const templateData = appliedCorrections?.template;
  const correctedFields =
    consumerData?.fields?.map((field: any) => ({
      label: field.fieldLabel,
      oldValue: field.oldValue || "NA",
      newValue: field.newValue || "NA",
    })) ?? [];

  const handleNext = () => {
    generateBill(
      {},
      {
        onSuccess: (res) => {
          stepHelpers?.setStepData(3, {newBillId: res.result?.newBillId})
          onNext();
        },
      }
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* ---------------- Financial Impact ---------------- */}
      <Card className="border-blue-100 shadow-md overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-white border-b border-blue-100 pb-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                Financial Impact Analysis
              </CardTitle>
              <CardDescription>
                Comparison of bill amount before and after corrections
              </CardDescription>
            </div>
            <Badge
              variant={difference > 0 ? "destructive" : "default"}
              className={cn(
                "text-sm px-3 py-1",
                difference > 0
                  ? "bg-red-100 text-red-700 border-red-200"
                  : "bg-green-100 text-green-700 border-green-200"
              )}
            >
              {difference > 0 ? "Increase" : "Decrease"} of{" "}
              {0}%
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-x divide-gray-100">
            <div className="p-6 bg-gray-50/50">
              <p className="text-sm font-medium text-gray-500 mb-1">
                Original Bill Amount
              </p>
              <div className="text-3xl font-bold text-gray-700">
                {currency}
                {originalTotal}
              </div>
            </div>

            <div className="p-6 bg-white">
              <p className="text-sm font-medium text-blue-600 mb-1">
                New Bill Amount
              </p>
              <div className="text-3xl font-bold text-blue-700">
                {currency}
                {newTotal}
              </div>
            </div>

            <div
              className={cn(
                "p-6",
                difference > 0 ? "bg-red-50/30" : "bg-green-50/30"
              )}
            >
              <p className="text-sm font-medium text-gray-500 mb-1">
                Net Difference
              </p>
              <div
                className={cn(
                  "text-3xl font-bold flex items-center gap-2",
                  difference > 0 ? "text-red-600" : "text-green-600"
                )}
              >
                {difference > 0 ? "+" : ""}
                {currency}
                {difference}
                {difference > 0 ? <TrendingUp /> : <TrendingDown />}
              </div>
            </div>
          </div>

          <div className="border-t p-6 bg-blue-50/30">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600" />
              <p className="text-sm text-gray-600">
                Upon approval, the original bill will be marked as{" "}
                <b>Corrected</b> and a new bill will be generated.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---------------- Corrections ---------------- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex gap-2">
            <FileText className="h-4 w-4" /> Applied Corrections
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* -------- Consumption -------- */}
          {selectedTypes.includes("consumption") && (
            <div className="p-3 bg-blue-50 border rounded-lg">
              <div className="flex justify-between mb-3">
                <span className="font-bold">
                  {consumption?.sectionTitle ?? "Consumption Correction"}
                </span>
                <Badge>{consumption?.statusBadge ?? "NA"}</Badge>
              </div>

              {consumptionMeters.map((meter) => (
                <div key={meter.id} className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <meter.icon className="h-3 w-3" />
                    <span className="text-sm font-medium">{meter.title}</span>
                  </div>

                  <div className="pl-4 space-y-1 text-sm border-l-2">
                    <div className="flex justify-between">
                      <span>Current Reading</span>
                      <span>
                        <s>{meter.currentReading.original}</s> →{" "}
                        <b>{meter.currentReading.corrected}</b>
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span>Consumption</span>
                      <span>
                        <s>{meter.consumption.original}</s> →{" "}
                        <b>{meter.consumption.corrected}</b>
                      </span>
                    </div>

                    <div className="text-xs italic mt-2">"{meter.reason}"</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* -------- Penalty -------- */}
          {selectedTypes.includes("penalty") && (
            <div className="p-3 bg-red-50 border rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="font-bold">
                  {lateFees?.sectionTitle ?? "Penalty Correction"}
                </span>
                <Badge>{lateFees?.statusBadge ?? "NA"}</Badge>
              </div>

              <div className="flex justify-between text-sm">
                <span>Late Fee</span>
                <span>
                  <s>
                    {currency}
                    {lateFeeData.original}
                  </s>{" "}
                  →{" "}
                  <b>
                    {currency}
                    {lateFeeData.corrected}
                  </b>
                </span>
              </div>

              <div className="text-xs italic mt-2">"{lateFeeData.reason}"</div>
            </div>
          )}

          {/* -------- Data -------- */}
          {selectedTypes.includes("data") && (
            <div className="p-3 bg-cyan-50 border rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="font-bold">
                  {consumerData?.sectionTitle ?? "Data Correction"}
                </span>
                <Badge>{consumerData?.statusBadge ?? "NA"}</Badge>
              </div>

              {correctedFields.map((field) => (
                <div key={field.label} className="grid grid-cols-3 text-sm">
                  <span>{field.label}</span>
                  <span>{field.oldValue}</span>
                  <b>{field.newValue}</b>
                </div>
              ))}
              <span>
                <i>"{consumerData?.reason}"</i>
              </span>
            </div>
          )}

          {/* -------- Service (Hardcoded) -------- */}
          {selectedTypes.includes("service") && (
            <div className="p-3 bg-purple-50 border rounded-lg space-y-3">
              {/* Header */}
              <div className="flex justify-between items-center">
                <span className="font-bold">
                  {serviceChargesData?.sectionTitle ?? "Service Charges"}
                </span>
                <Badge variant="outline">
                  {serviceChargesData?.statusBadge ?? "NA"}
                </Badge>
              </div>

              {/* Charges List */}
              <div className="space-y-2">
                {serviceChargesData?.allCharges?.map((charge, index) => {
                  const isRemoved = charge.status === "Removed";

                  return (
                    <div
                      key={index}
                      className={`flex justify-between items-center`}
                    >
                      {/* Left: Charge Info */}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {charge.chargeName}
                        </span>
                        <span
                          className={`text-xs ${
                            isRemoved ? "text-red-600" : "text-gray-500"
                          }`}
                        >
                          Amount: ${charge.amount}
                        </span>
                      </div>

                      {/* Right: Status Badge */}
                      {isRemoved && (
                        <Badge variant="destructive" className="text-xs">
                          Excluded
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* -------- Template (Hardcoded) -------- */}
          {selectedTypes.includes("template") && (
            <div className="p-3 bg-indigo-50 border rounded-lg space-y-3">
              {/* Header */}
              <div className="flex justify-between items-center">
                <span className="font-bold">
                  {templateData?.sectionTitle ?? "Template Change"}
                </span>
                <Badge variant="outline">
                  {templateData?.statusBadge ?? "NA"}
                </Badge>
              </div>

              {/* Template Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Old Template */}
                <div className="">
                  <span className="text-sm  font-medium">
                    Old Template
                  </span>

                  <div className="mt-2 space-y-0.5">
                    <span className="text-xs text-gray-600">
                      <s>{templateData?.oldTemplateName ?? "-"}</s>
                    </span>
                  </div>
                </div>

                {/* New Template */}
                <div className="">
                  <span className="text-sm   font-medium">
                    New Template
                  </span>

                  <div className="mt-2 space-y-0.5">
                    <span className="text-xs text-gray-600">
                      {templateData?.newTemplateName ?? "-"}
                    </span>
                  </div>
                </div>
                <div className="text-xs italic mt-2">"{templateData?.reason}"</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          Previous
        </Button>
        <Button onClick={handleNext}>Next</Button>
      </div>
    </div>
  );
}
